import { type JSX, useCallback, useMemo } from 'react';
import { CheckState } from '@/storybook/components/FormCollection/Checkbox/Checkbox';
import DropdownCheckbox from '@/storybook/components/FormCollection/components/DropdownCheckbox/DropdownCheckbox';
import LeafRow from '../Components/LeafRow';
import { NestedOptions, getSelectedCountInSubtree, getBadgeForIndicationMode } from '../TreeSelect.helpers';
type UseOldTreeSelectRenderingParams<T extends string> = {
  value: T[];
  searchInput: string;
  searchResults: Set<string>;
  userOpenedBranches: Set<string>;
  selectionMode: 'standard' | 'indication';
  disabled?: boolean;
  treeListItemClassName?: string;
  handleClickOption: (optionPath: string) => void;
  handleToggleBranch: (optionPath: string, currentState: boolean) => void;
};

export function useOldTreeSelectRendering<T extends string>({
  value,
  searchInput,
  searchResults,
  userOpenedBranches,
  selectionMode,
  disabled,
  treeListItemClassName,
  handleClickOption,
  handleToggleBranch,
}: UseOldTreeSelectRenderingParams<T>) {
  const selectedSet = useMemo(() => new Set<string>(value ?? []), [value]);

  const nestedElementProperties = useCallback(
    (properties: { path: string; checked: CheckState; subOptions: NestedOptions }) => {
      const shouldRender = searchInput ? searchResults.has(properties.path) : true;
      const hasSubOptions = Object.keys(properties.subOptions).length > 0;
      const isInSearchResults = searchInput && searchResults.has(properties.path);
      const isInUserOpenedBranches = userOpenedBranches.has(properties.path);
      const isCurrentBranchOpen = isInSearchResults || isInUserOpenedBranches;
      const selectedCount = getSelectedCountInSubtree(properties, selectedSet, selectionMode === 'indication');
      return { shouldRender, hasSubOptions, isCurrentBranchOpen, selectedCount };
    },
    [searchInput, searchResults, userOpenedBranches, selectionMode, selectedSet],
  );

  const renderNestedOptions = useCallback(
    (nestedOptionsToRender: NestedOptions) => {
      return Object.entries(nestedOptionsToRender).flatMap(([optionLabel, properties]): JSX.Element[] => {
        const { shouldRender, hasSubOptions, isCurrentBranchOpen, selectedCount } = nestedElementProperties(properties);
        if (!shouldRender) return [];

        const badgeContent = getBadgeForIndicationMode({
          isIndication: selectionMode === 'indication',
          selectedSet,
          properties,
          hasSubOptions,
          isCurrentBranchOpen,
          selectedCount,
        });

        return [
          hasSubOptions ? (
            <DropdownCheckbox
              key={properties.path}
              label={optionLabel}
              optionPath={properties.path}
              onCheck={handleClickOption}
              onOpenDropdown={handleToggleBranch}
              isOpen={isCurrentBranchOpen}
              checked={properties.checked}
              className={treeListItemClassName}
              rightAdornment={badgeContent}
              isDisabled={disabled}
            >
              {renderNestedOptions(properties.subOptions)}
            </DropdownCheckbox>
          ) : (
            <LeafRow
              key={properties.path}
              optionLabel={optionLabel}
              properties={properties}
              handleClickOption={handleClickOption}
              badgeContent={badgeContent}
              isDisabled={disabled}
            />
          ),
        ];
      });
    },
    [
      nestedElementProperties,
      handleClickOption,
      handleToggleBranch,
      selectionMode,
      selectedSet,
      treeListItemClassName,
      disabled,
    ],
  );

  return { renderNestedOptions };
}
